/**
 * Templates Query Hooks
 * 
 * React Query hooks for fetching template data.
 * 
 * @module hooks/queries/useTemplates
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { templatesService, Template, CreateTemplateDto, UpdateTemplateDto, TemplateApp } from '../../services/templates.service';

// =============================================================================
// QUERY KEYS
// =============================================================================

// Extend queryKeys with templates
export const templateKeys = {
  all: ['templates'] as const,
  lists: () => [...templateKeys.all, 'list'] as const,
  active: () => [...templateKeys.all, 'active'] as const,
  detail: (id: number) => [...templateKeys.all, 'detail', id] as const,
  apps: (id: number) => [...templateKeys.all, 'apps', id] as const,
  usersCount: (id: number) => [...templateKeys.all, 'users-count', id] as const,
};

// =============================================================================
// QUERY HOOKS
// =============================================================================

/**
 * Hook to fetch all templates
 */
export const useAllTemplates = () => {
  return useQuery({
    queryKey: templateKeys.lists(),
    queryFn: () => templatesService.getAll(),
  });
};

/**
 * Hook to fetch active templates only
 */
export const useActiveTemplates = () => {
  return useQuery({
    queryKey: templateKeys.active(),
    queryFn: () => templatesService.getActive(),
  });
};

/**
 * Hook to fetch a single template by ID
 */
export const useTemplate = (id: number) => {
  return useQuery({
    queryKey: templateKeys.detail(id),
    queryFn: () => templatesService.getById(id),
    enabled: !!id,
  });
};

/**
 * Hook to fetch template apps with full details
 */
export const useTemplateApps = (id: number) => {
  return useQuery({
    queryKey: templateKeys.apps(id),
    queryFn: () => templatesService.getTemplateApps(id),
    enabled: !!id,
  });
};

/**
 * Hook to fetch users count for a template
 */
export const useTemplateUsersCount = (id: number) => {
  return useQuery({
    queryKey: templateKeys.usersCount(id),
    queryFn: () => templatesService.getTemplateUsersCount(id),
    enabled: !!id,
  });
};

// =============================================================================
// MUTATION HOOKS
// =============================================================================

/**
 * Hook to create a new template
 */
export const useCreateTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateTemplateDto) => templatesService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateKeys.all });
    },
  });
};

/**
 * Hook to update a template
 */
export const useUpdateTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateTemplateDto }) =>
      templatesService.update(id, data),
    onSuccess: (updatedTemplate) => {
      queryClient.setQueryData(
        templateKeys.detail(updatedTemplate.id),
        updatedTemplate
      );
      queryClient.invalidateQueries({ queryKey: templateKeys.lists() });
      queryClient.invalidateQueries({ queryKey: templateKeys.active() });
    },
  });
};

/**
 * Hook to delete a template
 */
export const useDeleteTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => templatesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateKeys.all });
    },
  });
};

/**
 * Hook to toggle template active status
 */
export const useToggleTemplateActive = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => templatesService.toggleActive(id),
    onSuccess: (updatedTemplate) => {
      queryClient.setQueryData(
        templateKeys.detail(updatedTemplate.id),
        updatedTemplate
      );
      queryClient.invalidateQueries({ queryKey: templateKeys.lists() });
      queryClient.invalidateQueries({ queryKey: templateKeys.active() });
    },
  });
};

/**
 * Hook to add an app to a template
 */
export const useAddTemplateApp = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ templateId, appId, roleId }: { templateId: number; appId: number; roleId?: number }) =>
      templatesService.addApp(templateId, appId, roleId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: templateKeys.detail(variables.templateId) });
      queryClient.invalidateQueries({ queryKey: templateKeys.apps(variables.templateId) });
    },
  });
};

/**
 * Hook to remove an app from a template
 */
export const useRemoveTemplateApp = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ templateId, appId }: { templateId: number; appId: number }) =>
      templatesService.removeApp(templateId, appId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: templateKeys.detail(variables.templateId) });
      queryClient.invalidateQueries({ queryKey: templateKeys.apps(variables.templateId) });
    },
  });
};

// =============================================================================
// EXPORTS
// =============================================================================

export type { Template, CreateTemplateDto, UpdateTemplateDto, TemplateApp };

